import { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPut } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Heart, AlertTriangle, Phone, User as UserIcon, School, Clock, CheckCircle2, ArrowRight, History, Archive, Search, Filter, MapPin, X } from 'lucide-react';
import { toast } from 'sonner';

const SEVERITY_STYLES = {
  critical: { bg: 'bg-rose-50', border: 'border-rose-300', text: 'text-rose-700', icon: 'text-rose-600' },
  high:     { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-700', icon: 'text-amber-600' },
  moderate: { bg: 'bg-yellow-50', border: 'border-yellow-300', text: 'text-yellow-700', icon: 'text-yellow-600' },
};
const STATUS_STYLES = {
  open:         { label: 'Open',         bg: 'bg-rose-100',     text: 'text-rose-700' },
  acknowledged: { label: 'Acknowledged', bg: 'bg-blue-100',     text: 'text-blue-700' },
  actioned:     { label: 'Actioned',     bg: 'bg-emerald-100',  text: 'text-emerald-700' },
  closed:       { label: 'Closed',       bg: 'bg-slate-100',    text: 'text-slate-600' },
};
const MIN_NOTE = 20;

function fmtDT(s) {
  try { return new Date(s).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return s || ''; }
}

/** Parent contact pills — primary + secondary, click-to-call. */
function ParentContacts({ pc }) {
  const items = [];
  if (pc?.primary_phone)   items.push({ phone: pc.primary_phone,   relation: pc.primary_relationship || 'Parent',   name: pc.primary_name });
  if (pc?.secondary_phone) items.push({ phone: pc.secondary_phone, relation: pc.secondary_relationship || 'Parent', name: pc.secondary_name });
  if (items.length === 0) {
    return (
      <div className="text-[11px] text-slate-500 font-body italic" data-testid="parent-contacts-missing">
        No parent contact on file. Ask the student to add one in Profile → "For your safety".
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-2" data-testid="parent-contacts">
      {items.map((it, i) => (
        <a key={i}
          href={`tel:${it.phone}`}
          data-testid={`parent-tel-${i}`}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-heading hover:bg-emerald-100 transition-colors">
          <Phone className="w-3.5 h-3.5" />
          <span className="capitalize">{it.relation}{it.name ? ` (${it.name})` : ''}</span>
          <span className="font-mono text-[11px] text-emerald-800">{it.phone}</span>
        </a>
      ))}
    </div>
  );
}

function AlertRow({ alert: a, onUpdate, isOpen, onToggle, mode = 'active' }) {
  const sev = SEVERITY_STYLES[a.severity] || SEVERITY_STYLES.moderate;
  const stat = STATUS_STYLES[a.status] || STATUS_STYLES.open;
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(null);
  const noteTooShort = note.trim().length < MIN_NOTE;
  const history = Array.isArray(a.history) ? a.history : [];

  const transition = async (toStatus) => {
    if (noteTooShort) { toast.error(`Please write at least ${MIN_NOTE} characters explaining what was done.`); return; }
    setSubmitting(toStatus);
    try {
      await apiPut(`/admin/emotional-alerts/${a.alert_id}`, { status: toStatus, support_notes: note.trim() });
      toast.success(`Ticket marked ${toStatus}.`);
      setNote(''); onUpdate();
    } catch (err) {
      toast.error((err?.message || '').replace(/^HTTP \d+:\s*/, '') || `Could not mark ${toStatus}.`);
    } finally { setSubmitting(null); }
  };

  const locationStr = [a.user_city, a.user_state].filter(Boolean).join(', ');

  return (
    <Card data-testid={`alert-${a.alert_id}`}
      className={`rounded-2xl border-l-4 ${sev.border} ${mode === 'active' ? sev.bg : 'bg-white'} overflow-hidden`}>
      {/* Compact summary row — message shown ONLY when collapsed; expanded view drops it to avoid duplication */}
      <button onClick={() => onToggle(a.alert_id)} className="w-full text-left p-4 hover:bg-white/60 transition-colors">
        <div className="flex items-start gap-3">
          <AlertTriangle className={`w-5 h-5 ${sev.icon} flex-shrink-0 mt-0.5`} />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className={`text-[10px] font-heading uppercase tracking-wider ${sev.text}`}>{a.severity}</span>
              <Badge className={`${stat.bg} ${stat.text} text-[10px] font-heading`}>{stat.label}</Badge>
              <span className="text-[10px] text-slate-500 font-body flex items-center gap-1">
                <Clock className="w-3 h-3" />{fmtDT(a.created_at)}
              </span>
              {history.length > 0 && (
                <span className="text-[10px] text-slate-500 font-body flex items-center gap-1">
                  <History className="w-3 h-3" />{history.length} action{history.length === 1 ? '' : 's'}
                </span>
              )}
            </div>
            <p className="font-heading text-sm font-semibold text-slate-900">{a.user_name || a.user_email}</p>
            <p className="text-[11px] text-slate-500 font-body flex flex-wrap items-center gap-x-2">
              <span>{a.user_email}</span>
              <span>·</span>
              <span>Class {a.user_class || '?'}</span>
              {a.user_school && <><span>·</span><span className="inline-flex items-center gap-1"><School className="w-3 h-3" />{a.user_school}</span></>}
              {locationStr && <><span>·</span><span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{locationStr}</span></>}
            </p>
            {/* Show message ONLY when collapsed — avoids the duplicate "Full message" inside expanded view */}
            {!isOpen && (
              <p className="text-sm text-slate-700 font-body mt-2 italic line-clamp-2">"{a.message_excerpt}"</p>
            )}
            {!isOpen && (a.category || a.detected_by) && (
              <div className="flex flex-wrap gap-1 mt-2">
                {a.category && (
                  <span data-testid={`alert-category-${a.alert_id}`}
                        className={`text-[10px] px-2 py-0.5 rounded-full ${sev.bg} ${sev.text} font-body border ${sev.border}`}>
                    {a.category.replace(/_/g, ' ')}
                  </span>
                )}
                {a.detected_by && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-body border border-slate-200">
                    {a.detected_by === 'keyword_only' ? 'keyword match' : 'AI classifier'}
                  </span>
                )}
              </div>
            )}
          </div>
          <ArrowRight className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
        </div>
      </button>

      {isOpen && (
        <div className="border-t border-slate-200 bg-white p-4 space-y-4" data-testid={`alert-expanded-${a.alert_id}`}>
          {/* Message — single canonical placement when expanded */}
          <div>
            <p className="text-[10px] font-heading uppercase tracking-wider text-slate-500 mb-1">Student's message</p>
            <blockquote className={`px-4 py-3 rounded-xl ${sev.bg} border ${sev.border}`}>
              <p className="text-sm text-slate-800 font-body italic leading-relaxed">"{a.message_excerpt}"</p>
              <p className="text-[10px] text-slate-500 font-body mt-2">
                First 160 characters only. Open the child's conversation if you need the full context.
              </p>
            </blockquote>
          </div>

          {/* Parent contact — primary action surface for safety */}
          <div>
            <p className="text-[10px] font-heading uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
              <Phone className="w-3 h-3" /> Reach the parents
            </p>
            <ParentContacts pc={a.parent_contacts} />
          </div>

          {/* Handling history timeline — permanent audit trail */}
          {history.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3" data-testid={`history-${a.alert_id}`}>
              <h4 className="font-heading text-xs font-semibold text-slate-700 flex items-center gap-1.5 mb-2">
                <History className="w-3.5 h-3.5" /> Handling history ({history.length})
              </h4>
              <ol className="space-y-2">
                {history.map((h, i) => {
                  const hStat = STATUS_STYLES[h.to_status] || STATUS_STYLES.open;
                  return (
                    <li key={i} className="relative pl-4 border-l-2 border-slate-300">
                      <span className="absolute -left-[5px] top-1 w-2 h-2 rounded-full bg-slate-400" />
                      <div className="flex flex-wrap items-baseline gap-2 text-[11px]">
                        <Badge className={`${hStat.bg} ${hStat.text} text-[10px] font-heading`}>{hStat.label}</Badge>
                        <span className="text-slate-500 font-body">by {h.actor}</span>
                        <span className="text-slate-400 font-body">· {fmtDT(h.timestamp)}</span>
                      </div>
                      {h.note && <p className="text-xs text-slate-700 font-body mt-1 whitespace-pre-wrap leading-relaxed">{h.note}</p>}
                    </li>
                  );
                })}
              </ol>
            </div>
          )}

          {/* Action form — only for non-closed tickets */}
          {a.status !== 'closed' ? (
            <div className="space-y-2 pt-1">
              <div className="flex items-center justify-between">
                <label className="text-xs font-heading font-semibold text-slate-700">
                  What did you do? <span className="text-rose-500">*</span>
                </label>
                <span className={`text-[10px] font-body ${noteTooShort ? 'text-rose-500' : 'text-emerald-600'}`}>
                  {note.trim().length}/{MIN_NOTE} min
                </span>
              </div>
              <Textarea data-testid={`note-${a.alert_id}`}
                value={note} onChange={e => setNote(e.target.value)}
                placeholder="Required: who was contacted (mother/father/counsellor), date & time, what they said, next step. No silent closes."
                className="text-sm rounded-xl min-h-[90px]" />
              <div className="flex flex-wrap gap-2 items-center pt-1">
                <Button size="sm" variant="outline" data-testid={`acknowledge-${a.alert_id}`}
                  disabled={submitting !== null || noteTooShort} onClick={() => transition('acknowledged')}>
                  {submitting === 'acknowledged' ? 'Saving…' : 'Mark Acknowledged'}
                </Button>
                <Button size="sm" data-testid={`actioned-${a.alert_id}`}
                  disabled={submitting !== null || noteTooShort} onClick={() => transition('actioned')}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white">
                  {submitting === 'actioned' ? 'Saving…' : 'Mark Actioned'}
                </Button>
                <Button size="sm" variant="ghost" data-testid={`closed-${a.alert_id}`}
                  disabled={submitting !== null || noteTooShort} onClick={() => transition('closed')}>
                  {submitting === 'closed' ? 'Saving…' : 'Close'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-50 border border-emerald-200">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <p className="text-xs text-emerald-800 font-body">Ticket closed. Full history preserved above — kept permanently for audit.</p>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export default function EmotionalSafetyDashboard() {
  const [data, setData] = useState(null);
  const [filterSeverity, setFilterSeverity] = useState('');
  const [search, setSearch] = useState('');
  const [school, setSchool] = useState('');
  const [classLevel, setClassLevel] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const load = async () => {
    const params = new URLSearchParams();
    if (filterSeverity) params.set('severity', filterSeverity);
    if (search.trim()) params.set('q', search.trim());
    if (school.trim()) params.set('school', school.trim());
    if (classLevel.trim()) params.set('class_level', classLevel.trim());
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    params.set('limit', '500');
    try {
      const r = await apiGet(`/admin/emotional-alerts?${params.toString()}`);
      setData(r);
    } catch (e) { toast.error(e.message?.includes('403') ? 'Admin/Support access only' : 'Failed to load alerts'); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterSeverity, search, school, classLevel, dateFrom, dateTo]);

  const toggle = (id) => setExpanded(cur => cur === id ? null : id);
  const clearFilters = () => { setSearch(''); setSchool(''); setClassLevel(''); setDateFrom(''); setDateTo(''); setFilterSeverity(''); };
  const hasFilters = !!(search || school || classLevel || dateFrom || dateTo || filterSeverity);

  const { activeAlerts, historicalAlerts } = useMemo(() => {
    if (!data?.alerts) return { activeAlerts: [], historicalAlerts: [] };
    const active = [], historical = [];
    for (const a of data.alerts) {
      if (a.status === 'actioned' || a.status === 'closed') historical.push(a);
      else active.push(a);
    }
    return { activeAlerts: active, historicalAlerts: historical };
  }, [data]);

  if (!data) {
    return <div className="flex items-center justify-center py-16"><div className="w-8 h-8 border-4 border-rose-500 border-t-transparent rounded-full animate-spin" /></div>;
  }
  const { summary } = data;

  return (
    <div className="space-y-6" data-testid="safety-dashboard">
      {/* Header banner */}
      <Card className="rounded-3xl border-2 border-rose-200 overflow-hidden">
        <div className="bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50 p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white shadow-md flex items-center justify-center flex-shrink-0">
              <Heart className="w-7 h-7 text-rose-500" />
            </div>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wider font-heading text-rose-600 mb-1">Emotional Safety</p>
              <h2 className="font-heading text-xl sm:text-2xl font-bold text-slate-900">Students reaching out for emotional help</h2>
              <p className="text-sm text-slate-700 font-body mt-1 leading-relaxed">
                Every flagged conversation stays here forever — auditable record of who was reached, when, and the outcome.
                Reach the parents directly via the click-to-call number on each ticket.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
            <div className="bg-white/70 rounded-2xl p-3 text-center backdrop-blur-sm">
              <p className="text-[10px] uppercase tracking-wider text-rose-600 font-heading">Open · Critical</p>
              <p data-testid="stat-critical" className="font-heading text-2xl font-bold text-rose-600">{summary.critical_open}</p>
            </div>
            <div className="bg-white/70 rounded-2xl p-3 text-center backdrop-blur-sm">
              <p className="text-[10px] uppercase tracking-wider text-amber-600 font-heading">Open · High</p>
              <p data-testid="stat-high" className="font-heading text-2xl font-bold text-amber-600">{summary.high_open}</p>
            </div>
            <div className="bg-white/70 rounded-2xl p-3 text-center backdrop-blur-sm">
              <p className="text-[10px] uppercase tracking-wider text-yellow-600 font-heading">Open · Moderate</p>
              <p data-testid="stat-moderate" className="font-heading text-2xl font-bold text-yellow-600">{summary.moderate_open}</p>
            </div>
            <div className="bg-white/70 rounded-2xl p-3 text-center backdrop-blur-sm">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-heading">All-time tickets</p>
              <p data-testid="stat-total" className="font-heading text-2xl font-bold text-slate-900">{summary.total}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-4 text-[11px] font-body text-slate-600">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/70"><span className="w-1.5 h-1.5 rounded-full bg-rose-500" /> Open: <strong data-testid="stat-open">{summary.open}</strong></span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/70"><span className="w-1.5 h-1.5 rounded-full bg-blue-500" /> Acknowledged: <strong data-testid="stat-acknowledged">{summary.acknowledged || 0}</strong></span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/70"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Actioned: <strong data-testid="stat-actioned">{summary.actioned || 0}</strong></span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/70"><span className="w-1.5 h-1.5 rounded-full bg-slate-400" /> Closed: <strong data-testid="stat-closed">{summary.closed || 0}</strong></span>
          </div>
        </div>
      </Card>

      {/* Top-5 students */}
      {summary.by_user?.length > 0 && (
        <Card className="p-5 rounded-2xl border-slate-200" data-testid="top-students">
          <h3 className="font-heading text-sm font-semibold text-slate-800 flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-amber-500" /> Students mentioned most often
          </h3>
          <div className="space-y-1.5">
            {summary.by_user.map((u, i) => (
              <div key={u.user_id} className="flex items-center gap-3 px-3 py-2 rounded-xl bg-rose-50/40 border border-rose-100">
                <span className="w-6 h-6 rounded-full bg-rose-200 text-rose-700 flex items-center justify-center text-xs font-heading font-bold">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-body font-semibold text-slate-800 truncate">{u.name}</p>
                  <p className="text-[11px] text-slate-500 font-body truncate flex items-center gap-2">
                    <span><UserIcon className="w-3 h-3 inline mr-1" />{u.email}</span>
                    {u.class && <span><School className="w-3 h-3 inline mr-1" />Class {u.class}</span>}
                    {u.school && <span className="truncate">{u.school}</span>}
                  </p>
                </div>
                <Badge className="bg-rose-100 text-rose-700 text-[11px] font-heading flex-shrink-0">{u.alert_count} alerts</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card className="p-4 rounded-2xl border-slate-200 space-y-3" data-testid="filters-card">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input data-testid="filter-search" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by email, name, school or message…"
              className="pl-9 rounded-xl text-sm" />
          </div>
          <Button size="sm" variant="outline" onClick={() => setShowFilters(s => !s)} data-testid="filter-toggle">
            <Filter className="w-3.5 h-3.5 mr-1" /> {showFilters ? 'Hide' : 'More'} filters
          </Button>
          {hasFilters && (
            <Button size="sm" variant="ghost" onClick={clearFilters} data-testid="filter-clear">
              <X className="w-3.5 h-3.5 mr-1" /> Clear
            </Button>
          )}
        </div>
        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 pt-1" data-testid="advanced-filters">
            <Input data-testid="filter-school" value={school} onChange={e => setSchool(e.target.value)}
              placeholder="School name" className="rounded-xl text-sm" />
            <Input data-testid="filter-class" value={classLevel} onChange={e => setClassLevel(e.target.value)}
              placeholder="Class (e.g. 9)" className="rounded-xl text-sm" />
            <Input data-testid="filter-date-from" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="rounded-xl text-sm" />
            <Input data-testid="filter-date-to" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="rounded-xl text-sm" />
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] text-slate-500 font-body">Severity:</span>
          {['', 'critical', 'high', 'moderate'].map(sv => (
            <button key={sv || 'all-sev'} data-testid={`filter-severity-${sv || 'all'}`}
              onClick={() => setFilterSeverity(sv)}
              className={`px-3 py-1 rounded-full text-xs font-heading capitalize ${filterSeverity === sv ? 'bg-amber-500 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-amber-300'}`}>
              {sv || 'All'}
            </button>
          ))}
          <span className="ml-auto text-[11px] text-slate-500 font-body" data-testid="filtered-count">
            Showing {data.filtered_count ?? data.alerts.length} of {summary.total} all-time
          </span>
        </div>
      </Card>

      {/* Active section */}
      <section data-testid="section-active">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="font-heading text-base font-bold text-slate-900 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
            Needs your attention
            <Badge className="bg-rose-100 text-rose-700 text-[10px] font-heading ml-1">{activeAlerts.length}</Badge>
          </h3>
          <p className="text-[11px] text-slate-500 font-body">Open & acknowledged tickets — act now</p>
        </div>
        {activeAlerts.length === 0 ? (
          <Card className="p-10 rounded-3xl border-slate-200 text-center" data-testid="empty-active">
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
            <p className="font-heading text-base text-slate-700">All caught up here.</p>
            <p className="text-sm text-slate-500 font-body">No open tickets matching the current filters. Historical tickets remain below.</p>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="active-list">
            {activeAlerts.map(a => (
              <AlertRow key={a.alert_id} alert={a} onUpdate={load} isOpen={expanded === a.alert_id} onToggle={toggle} mode="active" />
            ))}
          </div>
        )}
      </section>

      {/* Historical section */}
      <section data-testid="section-historical">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="font-heading text-base font-bold text-slate-700 flex items-center gap-2">
            <Archive className="w-4 h-4 text-slate-500" />
            Historical tickets
            <Badge className="bg-slate-100 text-slate-600 text-[10px] font-heading ml-1">{historicalAlerts.length}</Badge>
          </h3>
          <p className="text-[11px] text-slate-500 font-body">Actioned & closed — permanent audit trail</p>
        </div>
        {historicalAlerts.length === 0 ? (
          <Card className="p-8 rounded-3xl border-dashed border-slate-200 text-center bg-slate-50/50" data-testid="empty-historical">
            <History className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-sm text-slate-600 font-body">No historical tickets matching these filters. Actioned and closed tickets are never deleted.</p>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="historical-list">
            {historicalAlerts.map(a => (
              <AlertRow key={a.alert_id} alert={a} onUpdate={load} isOpen={expanded === a.alert_id} onToggle={toggle} mode="historical" />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
